import { useContext, useEffect } from "react";
import toast from "react-hot-toast";
import { useRouter } from "next/router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    Collection,
    CollectionRuleResponse,
    CreateSourcePayload,
    CreateWebsiteScreenshotQueryParams,
    FindContentMetadataBodyParams,
    ImportByUrlQueryParams,
    RuleDescription,
    SetCollectionRulesBodyParams,
    UpdateCollectionBodyParams,
    MarkSourceAsNewPayload,
    ModifyCollectionContent,
    CollectionRuleType,
    RuleOperator,
    SourceType,
    ProfileCollectionLayout,
    CreateMediaUploadBodyParams,
    ContentResponse,
    FindDuplicateBodyParams
} from "@authory-packages/core";
import MixpanelTracker from "../../tracking/Mixpanel";
import { V3ButtonVariant } from "../Button/V3Button.types";
import { V3ModalUAC } from "../V3ModalUniversalAddContent/V3ModalUAC";
import { getAuthenticatedUserClient } from "../../utils/getAuthenticatedUser";
import { CookieContext } from "../../context/cookieContext";
import { AddSourceFormik } from "../V3ModalUniversalAddContent/AddSource/AddSource.utils";
import { markSourceAsNew } from "../../api/source";
import { V3GenericErrorHandler } from "../../api/error-handler";
import { Queries, getV3CollectionRulesKey } from "../../types/queries";
import { getV3CollectionRules } from "../../api/collections";
import { AliasesFormik } from "../EditAliases";
import { prepareURL } from "../../utils/prepareURL";
import { V3IconPlus } from "../Icons";
import { DropShadowBtnAddContent, UniversalAddContentPositioner } from "./UniversalAddContent.styles";
import { getUpdateCollectionMutation } from "../../utils/mutation-utils/collections/updateCollectionMutation";
import { getUpdateCollectionRulesMutation } from "../../utils/mutation-utils/collections/updateCollectionRulesMutation";
import { getAddRemoveItemFromCollectionMutation } from "../../utils/mutation-utils/collections/AddRemoveItemFromCollection";
import { useAddElementSkeletonTracker } from "../../context/addElementSkeletonContext";
import { getV3ProfileCollections } from "../../utils/getV3ProfileCollections";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { getCreateSourceMutation } from "../../utils/mutation-utils/sources/createSourcesMutation";
import { getUploadTemporaryImageMutation } from "../../utils/mutation-utils/upload-image/getUploadTemporaryImageMutation";
import { getCreateContentMutation } from "../../utils/mutation-utils/content/createContentMutation";
import { getImportWebsiteScreenshotMutation } from "../../utils/mutation-utils/content/importWebsite";
import { getFindContentFromFileMutation } from "../../utils/mutation-utils/content/findContentFromFile";
import { getFindContentSnippetsMutation } from "../../utils/mutation-utils/content/findContentSnippets";
import { getFindContentMutation } from "../../utils/mutation-utils/content/findContent";
import { getFindDuplicatesMutation } from "../../utils/mutation-utils/content/findDuplicates";
import { getRefetchArticleMutation } from "../../utils/mutation-utils/content/refetchArticleMutation";
import { FindDuplicateParams } from "../../utils/findContentByURL";
import { getUpdateUser } from "../../utils/mutation-utils/user/updateUser";
import { uniq } from "lodash";
import { FinalImportResult, UnifiedAddContentStages } from "../V3ModalUniversalAddContent/AddContent/AddContent.types";
import { generateCreateContentPayload } from "../../shared-logic/onCreateContent";
import { useTheme } from "styled-components";
import { createMediaUpload } from "../../api/manual-import";
import { AxiosError } from "axios";

const aggregateRules = (rules: RuleDescription[], type: CollectionRuleType) =>
    rules
        .filter((rule) => rule.type === type)
        .reduce(
            (curr, acc) => {
                return {
                    type: type,
                    params: uniq([...curr.params, ...acc.params])
                };
            },
            { type, params: [] }
        );

interface UniversalAddContentProps {
    collection?: Collection;
    onAddContentClose?: () => void;
    startState?: UnifiedAddContentStages;
    initialSourceType?: SourceType;
}

export const UniversalAddContent = ({
    collection,
    onAddContentClose,
    startState = UnifiedAddContentStages.ChooseType,
    initialSourceType
}: UniversalAddContentProps) => {
    const router = useRouter();
    const theme = useTheme();
    const token = useContext(CookieContext);
    const { authenticatedUser } = getAuthenticatedUserClient(token, router, true);
    const queryClient = useQueryClient();

    const { onScreen, addContentOpen, setAddContentOpen, setOnScreen } = useAddElementSkeletonTracker();

    useEffect(() => {
        return () => {
            setAddContentOpen(false);
            setOnScreen(false);
        };
    }, []);

    // Get the collection rules, if aplicable
    const rules = useQuery({
        queryKey: getV3CollectionRulesKey(token!, authenticatedUser?.data?.slug!, collection?.canonicalSlug || ""),
        queryFn: () => getV3CollectionRules({ token: token!, userSlug: authenticatedUser?.data?.slug!, collectionId: collection!.canonicalSlug }),
        enabled: !!collection && addContentOpen
    });

    const separatedRules: CollectionRuleResponse[] = [];

    rules.data?.rules.forEach((rule) => {
        rule.params.forEach((param) => separatedRules.push({ id: "", params: [param], type: rule.type }));
    });

    const mixpanelTracker = new MixpanelTracker(authenticatedUser.data);

    const trackEvent = (eventName: string, data: any) => {
        try {
            mixpanelTracker.trackEvent(eventName, { ...data });
        } catch {}
    };

    // Add item to collection mutation
    const addItemToCollectionMutation = getAddRemoveItemFromCollectionMutation(queryClient);

    // Update collection mutation
    const updateCollectionMutation = getUpdateCollectionMutation(queryClient, token!);
    // Create content mutation
    const createContentMutation = getCreateContentMutation(queryClient, token!);

    // Create source mutation
    const createSourceMutation = getCreateSourceMutation(queryClient);

    // Update Rules mutation
    const updateRulesMutation = getUpdateCollectionRulesMutation(queryClient, token!);

    // Save Settings Mutation
    const updateUserMutation = getUpdateUser(queryClient, token!);

    // Upload temporary image
    const uploadTemporaryImageMutation = getUploadTemporaryImageMutation();

    // Mark source as new
    const markSourceAsNewMutation = useMutation({
        mutationFn: markSourceAsNew,
        onError: V3GenericErrorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [Queries.User] });
        }
    });

    const onTemporaryImageUploadHandler = async (file: File) => {
        if (!authenticatedUser.data?.slug || !token) return "";

        const res = await uploadTemporaryImageMutation.mutateAsync({ file, token, userSlug: authenticatedUser.data?.slug, noResize: true });
        return res.url;
    };

    // Find content mutation
    const findContentMutation = getFindContentMutation();

    // Find content snippets
    const findContentSnippetsMutation = getFindContentSnippetsMutation();

    // Get website screenshot
    const importWebsiteScreenshotMutation = getImportWebsiteScreenshotMutation();

    const onEmailSourceSubmit = async (name: string) => {
        if (!token || !authenticatedUser.data) return;

        const newSource = await createSourceMutation.mutateAsync({
            token: token,
            userSlug: authenticatedUser.data.slug,
            source: {
                active: false,
                publicationName: name,
                url: undefined,
                importPastArticles: false
            }
        });

        return newSource;
    };

    const onAutommatedImportSubmit = async (formData: AddSourceFormik) => {
        if (!authenticatedUser.data || !token) return;

        let addSourcePayload: CreateSourcePayload = {
            url: formData.url,
            publicationName: formData.name,
            importEveryting: formData.importEveryting,
            somethingElse: formData.somethingElse,
            importPastArticles: true,
            active: formData.active
        };

        const coll = await createSourceMutation.mutateAsync({
            token: token,
            userSlug: authenticatedUser.data?.slug,
            source: addSourcePayload,
            skipInvalidateUser: true
        });

        queryClient.invalidateQueries({ queryKey: [Queries.User] });

        return coll;
    };

    const onAliasesSubmit = async (data: AliasesFormik) => {
        if (!token || !authenticatedUser.data) return;

        updateUserMutation.mutateAsync({
            userSlug: authenticatedUser.data?.slug,
            token,
            data
        });
    };

    const FINDT_CONTENT_MAX_WAIT_TIME = 60000;
    // The duplicate check scans the whole archive and sits on the critical path of every imported item,
    // so it gets a much shorter leash than the import calls
    const FIND_DUPLICATES_MAX_WAIT_TIME = 20000;

    const onFindContent = async (url: string, mimeType?: string) => {
        if (!token) return;

        const params = new ImportByUrlQueryParams();
        params.url = prepareURL(url);
        params.mimeType = mimeType;

        return findContentMutation.mutateAsync({ token, params, timeout: FINDT_CONTENT_MAX_WAIT_TIME });
    };

    const onWebsiteImport = async (url: string) => {
        if (!token) return;

        const params = new CreateWebsiteScreenshotQueryParams();
        params.url = prepareURL(url);

        try {
            return importWebsiteScreenshotMutation.mutateAsync({ token, params });
        } catch {}
    };

    const onFindContentSnippets = async (url: string) => {
        if (!token) return;

        const params = new FindContentMetadataBodyParams();
        params.urls = [prepareURL(url)];

        return findContentSnippetsMutation.mutateAsync({ token, params, timeout: FINDT_CONTENT_MAX_WAIT_TIME });
    };

    // Find duplicates
    const findDuplicatesMutation = getFindDuplicatesMutation();

    const onFindDuplicate = async ({ pageUrl, canonicalUrl, type }: FindDuplicateParams) => {
        if (!token) return;

        const params = new FindDuplicateBodyParams();
        params.pageUrl = prepareURL(pageUrl);
        params.canonicalUrl = canonicalUrl ? prepareURL(canonicalUrl) : undefined;
        params.type = type;

        return findDuplicatesMutation.mutateAsync({ token, params, timeout: FIND_DUPLICATES_MAX_WAIT_TIME });
    };

    // Refetch an existing item, used when the user replaces a duplicate
    const refetchArticleMutation = getRefetchArticleMutation(queryClient, token!);

    const onReplaceDuplicate = async (item: ContentResponse) => {
        if (!token || !authenticatedUser.data) return;

        try {
            await refetchArticleMutation.mutateAsync({ token, userSlug: authenticatedUser.data.slug, articleSlug: item.canonicalSlug });
            // The re-import is queued server side, so the item we have is the best we can show for now
            return item;
        } catch {
            // The mutation's error handler has already notified the user
            return;
        }
    };

    // Upload file mutation
    const uploadContentFromFileMutation = getFindContentFromFileMutation();

    const onFileChoosenHandler = (file: File) => {
        if (!token) return;

        return uploadContentFromFileMutation.mutateAsync({ file, token });
    };

    const onSourceExists = async (sourceId: string, notes?: string) => {
        if (!authenticatedUser.data || !token) return;

        const requestedUser = authenticatedUser.data?.slug;
        const requestedWebsite = encodeURIComponent(sourceId);

        const params: MarkSourceAsNewPayload = {
            notes
        };

        return markSourceAsNewMutation.mutateAsync({
            token,
            requestedUser,
            requestedWebsite,
            params
        });
    };

    const onManualImportSubmit = async (formData: FinalImportResult) => {
        if (!authenticatedUser.data || !token) return;

        const user = authenticatedUser.data;

        const onCreateSourceHandler = async (data: CreateSourcePayload) =>
            createSourceMutation.mutateAsync({
                token: token,
                userSlug: user.slug,
                source: data,
                skipInvalidateUser: true
            });

        // Shared between frontend and import plugin
        const createContentPayload = await generateCreateContentPayload(formData, onCreateSourceHandler);

        const res = await createContentMutation.mutateAsync({
            token,
            params: createContentPayload
        });

        // Collection context flow add item to collection if its not default collection
        if (collection && !collection.default) {
            const payload: ModifyCollectionContent = {
                articleIds: [res.canonicalSlug]
            };

            // Add item to collection
            await addItemToCollectionMutation.mutateAsync({
                token,
                userSlug: user.slug as string,
                collectionSlug: collection.canonicalSlug,
                payload,
                add: true,
                skipContentInvalidation: true,
                useContentDetaisOptimisticUpdate: true
            });
        }

        queryClient.invalidateQueries({ queryKey: [Queries.Content] });
        queryClient.invalidateQueries({ queryKey: [Queries.User] });
        queryClient.invalidateQueries({ queryKey: [Queries.ContentAnalytics] });
        queryClient.invalidateQueries({ queryKey: [Queries.Collections] });

        return res;
    };

    const onGetMediaUploadSignedUrl = async (params: CreateMediaUploadBodyParams) => {
        if (!token) return;

        try {
            const res = await createMediaUpload({ token, params });
            return res;
        } catch (e) {
            const error = e as AxiosError;
            if (error?.response?.status === 403) {
                toast.error(error?.response?.data.message);
            }
            return;
        }
    };

    const onRulesUpdate = async (operator: RuleOperator, rules: CollectionRuleResponse[]) => {
        if (!token || !authenticatedUser.data || !collection) return;

        // Update rules
        const finalRules: RuleDescription[] = [];

        const aggregatedKeywordRules = aggregateRules(rules, CollectionRuleType.Keyword);
        if (aggregatedKeywordRules.params.length) finalRules.push(aggregatedKeywordRules);

        const aggregatedTypeRules = aggregateRules(rules, CollectionRuleType.Type);
        if (aggregatedTypeRules.params.length) finalRules.push(aggregatedTypeRules);

        const aggregatedSourceRules = aggregateRules(rules, CollectionRuleType.Source);
        if (aggregatedSourceRules.params.length) finalRules.push(aggregatedSourceRules);

        const ruleParams = new SetCollectionRulesBodyParams();
        ruleParams.rules = finalRules;

        // Update operator
        const colParams = new UpdateCollectionBodyParams();
        colParams.operator = operator;

        const col = await updateCollectionMutation.mutateAsync({
            token,
            userSlug: authenticatedUser.data?.slug,
            params: colParams,
            collectionId: collection?.canonicalSlug
        });

        await updateRulesMutation.mutateAsync({
            token,
            userSlug: authenticatedUser.data?.slug,
            collectionId: collection.canonicalSlug,
            params: ruleParams
        });

        queryClient.invalidateQueries({ queryKey: [Queries.User] });

        toast.success("Auto-update settings saved!");

        return col;
    };

    const isAboutPage = !!router.query.aboutPage;
    const isPortfolioRoute = router.route === APPLICATION_ROUTES.PORTFOLIO_APP;
    const isPortfolioRouteAndNotAboutPage = isPortfolioRoute && !isAboutPage;
    const isCollectionRoute = router.route === `${APPLICATION_ROUTES.COLLECTION}/[item]`;
    const isContentArchiveRoute = router.route === APPLICATION_ROUTES.CONTENT;
    const { profileCollections } = getV3ProfileCollections(authenticatedUser.data?.collections || []);
    const hasCollectionsInProfile = !!profileCollections.length;
    const isProfileAndNotCollectionGridLayout = isPortfolioRoute && theme.customization.profileCollectionLayout !== ProfileCollectionLayout.grid;

    const allowedToShow =
        (isPortfolioRouteAndNotAboutPage && hasCollectionsInProfile && isProfileAndNotCollectionGridLayout) ||
        isCollectionRoute ||
        (isContentArchiveRoute && !!authenticatedUser.data?.articleCount);

    if (!authenticatedUser.data) return <></>;

    return (
        <>
            {allowedToShow && !onScreen && (
                <UniversalAddContentPositioner
                    onClick={() => {
                        setAddContentOpen(true);
                    }}
                >
                    <DropShadowBtnAddContent
                        data-intercom-target="UniversalAddContent"
                        icon={<V3IconPlus />}
                        text={`Add work samples${!!collection ? (collection.default ? "" : " to this collection") : " to your archive"}`}
                        autoWidth
                        onClick={() => setAddContentOpen(true)}
                        variant={V3ButtonVariant.large}
                        minWidth={!!collection ? 260 : 160}
                        style={{ height: 48, padding: 20 }}
                    />
                </UniversalAddContentPositioner>
            )}
            {addContentOpen && (
                <V3ModalUAC
                    usedPlugin={authenticatedUser.data.usedPlugin || false}
                    modalOpen={addContentOpen}
                    onCloseHandler={() => {
                        onAddContentClose && onAddContentClose();
                        setAddContentOpen(false);
                    }}
                    sources={authenticatedUser.data.sources}
                    names={[
                        `${authenticatedUser.data.firstName} ${authenticatedUser.data.lastName}`.trim(),
                        ...(authenticatedUser.data.aliases || [])
                    ]}
                    onAutommatedImportSubmit={onAutommatedImportSubmit}
                    onEmailSourceSubmit={onEmailSourceSubmit}
                    onAliasesSubmit={onAliasesSubmit}
                    onSourceExists={onSourceExists}
                    onFindContent={onFindContent}
                    onFindContentSnippets={onFindContentSnippets}
                    onFindDuplicate={onFindDuplicate}
                    onReplaceDuplicate={onReplaceDuplicate}
                    onManualImportSubmit={onManualImportSubmit}
                    onFileChoosenHandler={onFileChoosenHandler}
                    hasPaymentMethod={!!authenticatedUser.data.paymentMethod}
                    onTemporaryImageUploadHandler={onTemporaryImageUploadHandler}
                    onWebsiteImport={onWebsiteImport}
                    onGetMediaUploadSignedUrl={onGetMediaUploadSignedUrl}
                    trackEvent={trackEvent}
                    collection={collection}
                    rules={separatedRules}
                    onRulesUpdate={onRulesUpdate}
                    openingState={startState}
                    subscriptionPlanKey={authenticatedUser.data.subscriptionPlanKey}
                    articleCount={authenticatedUser.data.articleCount}
                    mediaUsage={authenticatedUser.data.mediaUsage}
                    isOnTrial={authenticatedUser.data.planSettings.isOnTrial}
                    numItems={authenticatedUser.data.articleCount}
                    initialSourceType={initialSourceType}
                    isAboveAudioLimit={authenticatedUser.data.mediaUsage.audio.used > authenticatedUser.data.mediaUsage.audio.limit}
                    isAboveVideoLimit={authenticatedUser.data.mediaUsage.video.used > authenticatedUser.data.mediaUsage.video.limit}
                />
            )}
        </>
    );
};
